import React from "react";
import {App as AntdApp, InputNumber, Typography} from "antd";

import api from "@/api/axios.js";

const {Text} = Typography;

/**
 * Gestione delle pagine di un volantino in pre-elaborazione: riordino per
 * trascinamento, spostamento a una posizione scelta, eliminazione con anteprima
 * d'impatto, aggiunta da PDF.
 *
 * Condiviso fra la pre-elaborazione volantino e la split-view variante, che hanno
 * barre laterali diverse ma comportamento identico.
 *
 * @param {string}   flyerId       volantino su cui operare (nella split-view è la variante)
 * @param {Array}    pages         elenco pagine nell'ordine corrente, come arriva da /preprocess
 * @param {Function} refetch       ricarica pagine e riquadri dal server
 * @param {Function} onAfterDelete invocato dopo un'eliminazione riuscita, per riallineare
 *                                 la pagina visualizzata
 */
export default function usePageManagement({flyerId, pages, refetch, onAfterDelete})
{
	const {message, modal} = AntdApp.useApp();
	const [addPagesOpen, setAddPagesOpen] = React.useState(false);

	// Stato E ref dello stesso flag, perché l'autosalvataggio dei riquadri ne ha bisogno
	// in due modi diversi: come dipendenza dell'effetto (per annullare un salvataggio
	// gia' in coda) e come lettura sincrona dentro il timer (per fermare un salvataggio
	// programmato un istante prima che l'operazione iniziasse).
	const [pagesBusy, setPagesBusy] = React.useState(false);
	const pagesBusyRef = React.useRef(false);

	const setBusy = React.useCallback((value) =>
	{
		pagesBusyRef.current = value;
		setPagesBusy(value);
	}, []);

	// Il server è l'unica fonte di verità sull'ordine: dopo ogni operazione si ricarica
	// tutto invece di aggiornare lo stato locale a mano, perché l'operazione sposta anche
	// i riquadri e le schede prodotto.
	const runPageMutation = React.useCallback(async (mutate, {success, failure}) =>
	{
		setBusy(true);
		try
		{
			await mutate();
			await refetch();
			message.success(success);
			return true;
		}
		catch (error)
		{
			message.error(error?.response?.data?.error || failure);
			return false;
		}
		finally
		{
			setBusy(false);
		}
	}, [refetch, message, setBusy]);

	const handlePagesDragEnd = React.useCallback(async (event) =>
	{
		const {active, over} = event;
		if (!over || active.id === over.id) return;

		const from = pages.findIndex((page) => page.name === active.id);
		const to = pages.findIndex((page) => page.name === over.id);
		if (from < 0 || to < 0) return;

		await runPageMutation(
			() => api.patch(`/api/flyers/${flyerId}/pages/reorder`, {from, to}),
			{success: "Pagine riordinate", failure: "Errore nel riordino delle pagine"}
		);
	}, [pages, flyerId, runPageMutation]);

	const handleDeletePage = React.useCallback(async (index) =>
	{
		// `null` = conteggio NON disponibile, ben distinto da "zero prodotti": il conteggio
		// fallito non deve mai diventare la rassicurazione "la pagina non contiene prodotti"
		// subito prima di un'operazione non annullabile.
		let impact = null;
		try
		{
			const {data} = await api.get(`/api/flyers/${flyerId}/pages/${index}/impact`);
			if (data?.ok) impact = data;
		}
		catch
		{
			// L'anteprima è un di più: se non arriva si chiede comunque conferma, dichiarando
			// però l'incertezza.
		}

		const detail = !impact
			? "Non è stato possibile verificare quanti prodotti contiene: potrebbero essercene."
			: impact.cards
				? `La pagina contiene ${impact.cards} prodotti, di cui ${impact.validatedCards} già convalidati.`
				: "La pagina non contiene prodotti.";

		modal.confirm({
			title: `Eliminare la pagina ${index + 1}?`,
			content: `${detail} L'operazione non è annullabile.`,
			okText: "Elimina pagina",
			cancelText: "Annulla",
			okButtonProps: {danger: true},
			onOk: async () =>
			{
				const deleted = await runPageMutation(
					() => api.delete(`/api/flyers/${flyerId}/pages/${index}`),
					{success: "Pagina eliminata", failure: "Errore nell'eliminazione della pagina"}
				);
				if (deleted) onAfterDelete?.();
			}
		});
	}, [flyerId, runPageMutation, modal, onAfterDelete]);

	const handleMovePageTo = React.useCallback((index) =>
	{
		// Il valore grezzo dell'InputNumber, non convertito: svuotando il campo AntD emette
		// `null`, e trasformarlo subito in numero lo appiattirebbe su 1 — confermando, la
		// pagina finirebbe in prima posizione senza che nessuno l'abbia chiesto.
		let target = index + 1;

		modal.confirm({
			title: `Spostare la pagina ${index + 1}`,
			content: (
				<div>
					<Text>Nuova posizione (da 1 a {pages.length}):</Text>
					<InputNumber
						min={1}
						max={pages.length}
						precision={0}
						defaultValue={index + 1}
						style={{width: "100%", marginTop: 8}}
						onChange={(value) => { target = value; }}
					/>
				</div>
			),
			okText: "Sposta",
			cancelText: "Annulla",
			onOk: async () =>
			{
				// Campo svuotato: nessuna posizione scelta, quindi nessuno spostamento.
				if (target === null || target === undefined || target === "") return;

				const requested = Number(target);
				if (!Number.isFinite(requested)) return;

				const to = Math.max(1, Math.min(Math.round(requested), pages.length)) - 1;
				if (to === index) return;

				await runPageMutation(
					() => api.patch(`/api/flyers/${flyerId}/pages/reorder`, {from: index, to}),
					{success: "Pagina spostata", failure: "Errore nello spostamento della pagina"}
				);
			}
		});
	}, [flyerId, runPageMutation, pages.length, modal]);

	const handlePageMenuAction = React.useCallback((key, index) =>
	{
		if (key === "delete") handleDeletePage(index);
		else if (key === "move") handleMovePageTo(index);
	}, [handleDeletePage, handleMovePageTo]);

	const handleAddPagesCompleted = React.useCallback(async () =>
	{
		setAddPagesOpen(false);
		setBusy(true);
		try
		{
			await refetch();
		}
		finally
		{
			setBusy(false);
		}
	}, [refetch, setBusy]);

	return {
		pagesBusy,
		pagesBusyRef,
		addPagesOpen,
		openAddPages: React.useCallback(() => setAddPagesOpen(true), []),
		closeAddPages: React.useCallback(() => setAddPagesOpen(false), []),
		handlePagesDragEnd,
		handlePageMenuAction,
		handleAddPagesCompleted
	};
}
